/**
 * Service Loader для Kernel
 * Завантажує та реєструє сервіси додатку з підтримкою DI
 */

import { IConfigLoader } from '@/Core/Contracts/Loaders/IConfigLoader'
import { ServiceContainer, type ServiceDefinition } from '@/Core/Utils/ServiceContainer'
import { BaseLoader } from './BaseLoader'

export interface ServiceMap {
    [key: string]: any
}

export class ServiceKernelLoader extends BaseLoader {
    private container: ServiceContainer
    private services: ServiceMap = {}
    private serviceDefinitions: ServiceDefinition[] = []

    constructor() {
        const config: IConfigLoader = {
            name: 'services',
            priority: 8,
            clientSafe: true,
            dependencies: [],
        }
        super(config)
        this.container = new ServiceContainer()
    }

    async load(): Promise<ServiceMap> {
        try {
            this.log('Starting service loading...')

            await this.loadCoreServices()
            await this.loadShipServices()
            await this.loadContainerServices()
            await this.instantiateServices()

            this.log(`Loaded ${Object.keys(this.services).length} services`)

            return this.services
        } catch (error) {
            this.log('Error loading services, using defaults: ' + error, 'error')
            return this.getDefaultServices()
        }
    }

    private async loadCoreServices(): Promise<void> {
        if (!this.isClient()) return

        try {
            const coreServices = import.meta.glob('@/Core/Services/*Service.{js,jsx,ts,tsx}', {
                eager: false,
            })

            await this.processServiceFilesAsync(coreServices, 'Core')
            this.log(`Loaded ${Object.keys(coreServices).length} Core services`)
        } catch (error) {
            this.log('Failed to load Core services: ' + error, 'warn')
        }
    }

    private async loadShipServices(): Promise<void> {
        if (!this.isClient()) return

        try {
            const shipServices = import.meta.glob('/src/Ship/Services/*Service.{js,jsx,ts,tsx}', {
                eager: false,
            })

            await this.processServiceFilesAsync(shipServices, 'Ship')
            this.log(`Loaded ${Object.keys(shipServices).length} Ship services`)
        } catch (error) {
            this.log('Failed to load Ship services: ' + error, 'warn')
        }
    }

    private async loadContainerServices(): Promise<void> {
        if (!this.isClient()) return

        try {
            const containerServices = import.meta.glob(
                '/src/Containers/*Section/*Container/Services/*Service.{js,jsx,ts,tsx}',
                { eager: false }
            )

            await this.processContainerServiceFilesAsync(containerServices)
            this.log(`Loaded ${Object.keys(containerServices).length} Container services`)
        } catch (error) {
            this.log('Failed to load Container services: ' + error, 'warn')
        }
    }

    private processServiceFiles(files: Record<string, any>, namespace: string): void {
        for (const [key, module] of Object.entries(files)) {
            const name = key
                .split('/')
                .pop()
                ?.replace(/\.(js|jsx|ts|tsx)$/, '')

            if (!name || !name.endsWith('Service')) {
                continue
            }

            const moduleObj = module as any

            // Шукаємо та реєструємо всі експортовані константи (об'єкти), пропускаємо класи та функції
            for (const exportName of Object.keys(moduleObj)) {
                const exportValue = moduleObj[exportName]
                
                // Пропускаємо класи (функції з prototype)
                if (typeof exportValue === 'function') {
                    continue
                }
                
                // Реєструємо тільки об'єкти (константи)
                if (exportValue && typeof exportValue === 'object') {
                    const serviceName = exportName.replace(/Service$/, '')
                    const fullServiceName = `${namespace}.${serviceName}`
                    
                    const definition: ServiceDefinition = {
                        name: fullServiceName,
                        factory: () => exportValue,
                        singleton: true,
                    }
                    
                    this.serviceDefinitions.push(definition)
                    this.container.register(definition)
                }
            }
        }
    }

    /**
     * Асинхронна версія processServiceFiles для lazy loading
     */
    private async processServiceFilesAsync(files: Record<string, () => Promise<any>>, namespace: string): Promise<void> {
        const loadPromises = Object.entries(files).map(async ([key, importFn]) => {
            const name = key
                .split('/')
                .pop()
                ?.replace(/\.(js|jsx|ts|tsx)$/, '')

            if (!name || !name.endsWith('Service')) {
                return
            }

            try {
                const module = await importFn()
                const moduleObj = module as any

                // Шукаємо та реєструємо всі експортовані константи
                for (const exportName of Object.keys(moduleObj)) {
                    const exportValue = moduleObj[exportName]
                    
                    if (typeof exportValue === 'function') {
                        continue
                    }
                    
                    if (exportValue && typeof exportValue === 'object') {
                        const serviceName = exportName.replace(/Service$/, '')
                        const fullServiceName = `${namespace}.${serviceName}`
                        
                        const definition: ServiceDefinition = {
                            name: fullServiceName,
                            factory: () => exportValue,
                            singleton: true,
                        }
                        
                        this.serviceDefinitions.push(definition)
                        this.container.register(definition)
                    }
                }
            } catch (error) {
                this.log(`Failed to load service from ${key}: ${error}`, 'warn')
            }
        })

        await Promise.all(loadPromises)
    }

    private processContainerServiceFiles(files: Record<string, any>): void {
        for (const [key, module] of Object.entries(files)) {
            const pathParts = key.split('/')

            if (pathParts[6] === undefined || !pathParts[6].includes('Service')) {
                continue
            }

            const sectionName = pathParts[3]?.replace('Section', '') || ''
            const containerName = pathParts[4]?.replace('Container', '') || ''
            const serviceName = pathParts[6].replace(/Service\.(js|jsx|ts|tsx)$/, '')

            const fullServiceName = `${sectionName}.${containerName}.${serviceName}`
            const serviceClassName = serviceName + 'Service'
            const moduleObj = module as any

            if (moduleObj[serviceClassName]) {
                const definition: ServiceDefinition = {
                    name: fullServiceName,
                    factory: () => new moduleObj[serviceClassName](),
                    singleton: true,
                }
                
                this.serviceDefinitions.push(definition)
                this.container.register(definition)
            }
        }
    }

    /**
     * Асинхронна версія processContainerServiceFiles для lazy loading
     */
    private async processContainerServiceFilesAsync(files: Record<string, () => Promise<any>>): Promise<void> {
        const loadPromises = Object.entries(files).map(async ([key, importFn]) => {
            const pathParts = key.split('/')

            if (pathParts[6] === undefined || !pathParts[6].includes('Service')) {
                return
            }

            const sectionName = pathParts[3]?.replace('Section', '') || ''
            const containerName = pathParts[4]?.replace('Container', '') || ''
            const serviceName = pathParts[6].replace(/Service\.(js|jsx|ts|tsx)$/, '')

            try {
                const module = await importFn()
                const fullServiceName = `${sectionName}.${containerName}.${serviceName}`
                const serviceClassName = serviceName + 'Service'
                const moduleObj = module as any

                if (moduleObj[serviceClassName]) {
                    const definition: ServiceDefinition = {
                        name: fullServiceName,
                        factory: () => new moduleObj[serviceClassName](),
                        singleton: true,
                    }
                    
                    this.serviceDefinitions.push(definition)
                    this.container.register(definition)
                }
            } catch (error) {
                this.log(`Failed to load container service from ${key}: ${error}`, 'warn')
            }
        })

        await Promise.all(loadPromises)
    }

    private async instantiateServices(): Promise<void> {
        for (const definition of this.serviceDefinitions) {
            try {
                // Використовуємо DI container для створення екземплярів
                const serviceInstance = this.container.resolve(definition.name)
                
                // Реєструємо тільки з коротким іменем (без префікса namespace)
                const shortName = definition.name.split('.').pop()
                if (shortName && !this.services[shortName]) {
                    this.services[shortName] = serviceInstance
                }

                // Ініціалізуємо сервіс (викликаємо onInit якщо є)
                await this.container.initializeService(serviceInstance)
            } catch (error) {
                this.log(`Failed to create service ${definition.name}: ${error}`, 'error')
            }
        }
    }

    private getDefaultServices(): ServiceMap {
        // Повертаємо базові сервіси за замовчуванням
        return {}
    }

    // Публічні методи для роботи з сервісами

    /**
     * Отримати сервіс за іменем
     */
    getService<T = any>(name: string): T | undefined {
        return this.services[name]
    }

    /**
     * Отримати сервіс через DI container (з автоматичною ін'єкцією залежностей)
     */
    resolve<T = any>(name: string): T {
        return this.container.resolve<T>(name)
    }

    /**
     * Перевірити наявність сервісу
     */
    hasService(name: string): boolean {
        return name in this.services
    }

    /**
     * Отримати всі імена сервісів
     */
    getServiceNames(): string[] {
        return Object.keys(this.services)
    }

    /**
     * Отримати Service Container
     */
    getContainer(): ServiceContainer {
        return this.container
    }

    /**
     * Отримати метадані всіх сервісів
     */
    getServiceMetadata() {
        return this.container.getAllMetadata()
    }

    /**
     * Перевірити здоров'я всіх сервісів
     */
    async checkServicesHealth(): Promise<Map<string, boolean>> {
        return await this.container.checkAllHealth()
    }

    /**
     * Отримати статистику сервісів
     */
    getStatistics() {
        return {
            ...this.container.getStatistics(),
            cacheStats: this.getCacheStats(),
        }
    }
}
